<template>
  <div>
    <section class="section is-main-section">
      <CardComponent>
        <div class="section has-text-centered">
          <h1 class="title">Manage your Media</h1>

          <!-- Media Type Tabs -->
          <b-tabs v-model="activeTabIndex" type="is-boxed" class="has-margin-bottom-15">
            <b-tab-item>
              <template #header>
                <span>Series <b-tag rounded>{{ getMediaTypeCount('series') }}</b-tag></span>
              </template>
            </b-tab-item>
            <b-tab-item>
              <template #header>
                <span>Movies <b-tag rounded>{{ getMediaTypeCount('movies') }}</b-tag></span>
              </template>
            </b-tab-item>
            <b-tab-item>
              <template #header>
                <span>TVC <b-tag rounded>{{ getMediaTypeCount('tvc') }}</b-tag></span>
              </template>
            </b-tab-item>
            <b-tab-item>
              <template #header>
                <span>Promo <b-tag rounded>{{ getMediaTypeCount('promo') }}</b-tag></span>
              </template>
            </b-tab-item>
            <b-tab-item>
              <template #header>
                <span>Filler <b-tag rounded>{{ getMediaTypeCount('filler') }}</b-tag></span>
              </template>
            </b-tab-item>
            <b-tab-item>
              <template #header>
                <span>GFX <b-tag rounded>{{ getMediaTypeCount('gfx') }}</b-tag></span>
              </template>
            </b-tab-item>
          </b-tabs>

          <div class="has-text-right">
            <b-button
              type="is-primary"
              :disabled="loading"
              icon-left="plus"
              class="has-margin-right-10"
              :class="{ 'is-loading': loading }"
              :to="{ name: 'ImportMediaFile' }"
              tag="router-link"
            >
              Import By File</b-button
            >
            <b-button
              type="is-primary"
              icon-left="plus"
              tag="router-link"
              :to="{ name: 'CreateMedia' }"
              >Add</b-button
            >
            <b-button
              type="is-warning"
              icon-left="history"
              class="has-margin-left-10"
              @click="showRecentChanges"
            >
              Recent Changes</b-button
            >
            <b-button
              type="is-info"
              icon-left="file-document-alert"
              class="has-margin-left-10"
              @click="showMissingAssetsReport = true"
            >
              Missing Assets</b-button
            >
            <b-button
              type="is-light"
              class="has-margin-left-10"
              @click="toggleSelectAll"
            >
              {{ allSelected ? 'Deselect All' : 'Select All' }}</b-button
            >
            <b-button
              v-if="anySelected"
              type="is-primary"
              :disabled="loading"
              class="has-margin-left-10"
              :class="{ 'is-loading': loading }"
              @click="deleteTitles"
            >
              Delete</b-button
            >
          </div>
          <b-field label="Filter by Channel">
            <BaseSelect v-model="channelFilter">
              <option value="">None</option>
              <option v-for="channel in channels">{{ channel }}</option>
            </BaseSelect>
          </b-field>
          <b-field class="has-margin-top-15">
            <b-input
              v-model="search"
              placeholder="Search..."
              type="search"
              icon="magnify"
            >
            </b-input>
          </b-field>
          <!-- Shortform-only: panel surfacing traffic-agent no_match files
               that the operator can turn into media rows in one click.
               Re-keys on the active media-type tab so each tab gets its
               own panel state (expanded/collapsed independently). -->
          <UnmatchedShortformUploadsPanel
            v-if="isShortform"
            :key="'unmatched-' + activeMediaType"
            :media-type="activeMediaType"
            :available-channels="channelObjects"
            :preselected-channel-name="channelFilter"
            @media-created="onMediaCreatedFromUpload"
          />
          <b-table
            :data="filteredMedia"
            :bordered="false"
            :striped="true"
            :narrowed="true"
            :hoverable="true"
            :loading="loading"
            :focusable="false"
            :mobile-cards="true"
            :paginated="true"
          >
            <template slot-scope="props">
              <b-table-column field="selected" label="" width="40">
                <b-checkbox v-model="props.row.selected" />
              </b-table-column>
              <b-table-column field="tx_ready" label="" width="30" centered>
                <!-- Dot only meaningful for non-series rows (movies + shortform);
                     series are containers, readiness lives on each episode.
                     Shortform uses a 5-state roll-up (grey/red/yellow/blue/green)
                     to give a richer at-a-glance signal across the new columns.
                     Movies/everything else keep the legacy 2-state dot. -->
                <span
                  v-if="isShortform && props.row.media_type !== 'series'"
                  class="tx-status-dot-inline"
                  :class="'shortform-state-' + shortformStatus(props.row).state"
                  :title="shortformStatus(props.row).label"
                ></span>
                <span
                  v-else-if="props.row.media_type !== 'series'"
                  class="tx-status-dot-inline"
                  :class="isMediaTxReady(props.row) ? 'tx-ready' : 'tx-missing'"
                  :title="isMediaTxReady(props.row) ? 'TX file present' : 'No TX file linked'"
                ></span>
              </b-table-column>
              <b-table-column field="title" label="Title">
                <router-link
                  class="media-title-link"
                  :to="{ name: 'EditMedia', params: { id: props.row.id } }"
                  >{{ props.row.title }}
                  {{
                    props.row.mediadata && props.row.mediadata.seriesNo
                      ? 'S' + props.row.mediadata.seriesNo
                      : ''
                  }}</router-link
                >
              </b-table-column>

              <b-table-column
                v-if="!isShortform"
                field="mediadata.ageRestriction"
                label="PG Rating"
              >
                {{ props.row.mediadata && props.row.mediadata.ageRestriction }}
              </b-table-column>
              <b-table-column field="duration" label="Duration" width="100">
                {{ formatDuration(props.row) }}
                <span v-if="isShortform && !hasDuration(props.row)" class="duration-missing" title="Duration missing — can't be scheduled until set">⚠</span>
              </b-table-column>
              <b-table-column field="metadata.suppliers" label="Supplier">
                {{
                  props.row.metadata && props.row.metadata.suppliers
                    ? props.row.metadata.suppliers.join(', ')
                    : ''
                }}
              </b-table-column>
              <!-- Shortform-only operational columns: tx filename, source link
                   indicator, approve toggle, preview. Hidden on Series tab so
                   the longform UX stays untouched. -->
              <b-table-column v-if="isShortform" field="tx_filename" label="TX Filename" width="220">
                <span v-if="props.row.tx_filename" class="tx-filename-cell" :title="props.row.tx_filename">
                  {{ props.row.tx_filename }}
                </span>
                <span v-else class="has-text-grey-light">—</span>
              </b-table-column>
              <b-table-column v-if="isShortform" field="source" label="Source" width="80" centered>
                <span v-if="hasSourceLinked(props.row)" class="source-linked" title="Source file linked">✓</span>
                <router-link
                  v-else
                  :to="{ name: 'EditMedia', params: { id: props.row.id } }"
                  class="source-link-action"
                  title="Open EditMedia to link a source file"
                >Link</router-link>
              </b-table-column>
              <b-table-column v-if="isShortform" field="transcode" label="Transcode" width="110" centered>
                <!-- Submit-only column for v1 — no live status polling here
                     to keep the table fast. Operator submits, sees toast,
                     opens EditMedia (or refreshes the list) to watch
                     progress in detail. -->
                <span v-if="!hasSourceLinked(props.row)" class="has-text-grey-light">—</span>
                <button
                  v-else
                  class="button is-small is-info"
                  :class="{ 'is-loading': transcodeSubmitting[props.row.id] }"
                  :disabled="!canSubmitTranscode(props.row) || !!transcodeSubmitting[props.row.id]"
                  @click.prevent="submitTranscode(props.row)"
                  :title="transcodeTooltip(props.row)"
                >
                  Submit
                </button>
              </b-table-column>
              <b-table-column v-if="isShortform" field="approved" label="Approve" width="110" centered>
                <!-- Explicit :value + @input rather than v-model — gives us
                     control over optimistic UI flip + revert on API failure.
                     No codebase precedent for v-model + @input on b-switch. -->
                <b-switch
                  :value="!!props.row.approved_for_playout"
                  size="is-small"
                  type="is-success"
                  :disabled="!canToggleApprove(props.row) || !!approveBusy[props.row.id]"
                  @input="onApproveToggle(props.row, $event)"
                  :title="approveTooltip(props.row)"
                />
              </b-table-column>
              <b-table-column v-if="isShortform" field="preview" label="" width="50" centered>
                <!-- Preview is purely about "can the browser play this".
                     MP4/M4V/TS/WebM yes; MXF/MOV no. Independent of
                     whether the file is BMS-transcoded — source MP4
                     plays fine in the browser. -->
                <button
                  class="button is-small is-light"
                  :disabled="!isBrowserPlayable(props.row)"
                  @click.prevent="openPreview(props.row)"
                  :title="previewTooltip(props.row)"
                >
                  ▶
                </button>
              </b-table-column>
              <b-table-column v-if="!isShortform" field="metadata.genres" label="Genres">
                {{
                  props.row.metadata && props.row.metadata.genres
                    ? props.row.metadata.genres.join(', ')
                    : ''
                }}
              </b-table-column>
              <b-table-column v-if="!isShortform" field="description" label="Synopsis" width="300">
                <span class="synopsis-cell">{{ props.row.description }}</span>
              </b-table-column>

              <!-- <b-table-column field="user" label="Rights Owner" width="250px">
                {{ props.row.mediadata.rightsOwner }}
              </b-table-column>

              <b-table-column field="user" label="Captured By" width="250px">
                {{ props.row.first_name }}
                {{ props.row.last_name }}
              </b-table-column>

              <b-table-column field="published" label="Published">
                {{ isPublished(props.row) ? 'Yes' : 'No' }}
                <a href="" @click.prevent="publish(props.row)">
                  <b-icon
                    v-if="isPublished(props.row)"
                    icon="toggle-switch"
                  ></b-icon>
                  <b-icon
                    v-if="!isPublished(props.row)"
                    icon="toggle-switch-off"
                  ></b-icon>
                </a>
              </b-table-column>

              <b-table-column field="featured" label="Featured">
                {{ isFeatured(props.row) ? 'Yes' : 'No' }}
                <a href="" @click.prevent="markAsFeatured(props.row)">
                  <b-icon
                    v-if="isFeatured(props.row)"
                    icon="toggle-switch"
                  ></b-icon>
                  <b-icon
                    v-if="!isFeatured(props.row)"
                    icon="toggle-switch-off"
                  ></b-icon>
                </a>
              </b-table-column> -->

              <b-table-column label="">
                <b-dropdown aria-role="list">
                  <button slot="trigger" class="button is-primary">
                    <span>Actions</span>
                    <b-icon icon="menu-down"></b-icon>
                  </button>

                  <b-dropdown-item aria-role="listitem" :has-link="true"
                    ><router-link
                      :to="{
                        name: 'EditMedia',
                        params: { id: props.row.id }
                      }"
                      >Edit</router-link
                    ></b-dropdown-item
                  >
                  <b-dropdown-item
                    v-if="props.row.media_type === 'series'"
                    aria-role="listitem"
                    :has-link="true"
                    ><router-link
                      :to="{
                        name: 'ManageEpisodes',
                        params: { id: props.row.id }
                      }"
                      >Manage Episodes</router-link
                    ></b-dropdown-item
                  >
                  <b-dropdown-item
                    aria-role="listitem"
                    @click="confirmDelete(props.row)"
                    >Delete</b-dropdown-item
                  >
                </b-dropdown>
              </b-table-column>
            </template>

            <template slot="empty">
              <section class="section">
                <div class="content has-text-grey has-text-centered">
                  <p>
                    <b-icon icon="emoticon-sad" size="is-medium"> </b-icon>
                  </p>
                  <p>No media yet.</p>
                </div>
              </section>
            </template>
          </b-table>
        </div>
      </CardComponent>
    </section>
    <b-modal
      :active.sync="showConfirmModal"
      trap-focus
      aria-role="dialog"
      aria-modal
      has-modal-card
      @close="showConfirmModal = false"
    >
      <div class="modal-card" style="width: auto">
        <header class="modal-card-head">
          <p class="modal-card-title">Confirm Delete?</p>
        </header>
        <section class="modal-card-body">
          Are you sure you want to delete this title?
        </section>
        <footer class="modal-card-foot" style="justify-content: flex-end">
          <button
            class="button"
            type="button"
            @click="showConfirmModal = false"
          >
            No
          </button>
          <button class="button is-primary" @click.prevent="deleteTitle()">
            Yes
          </button>
        </footer>
      </div>
    </b-modal>
    <ChangeManualQCStatusModal
      v-if="showChangeManualQCStatusModal"
      :show="showChangeManualQCStatusModal"
      :media="item"
      @close-modal="showChangeManualQCStatusModal = false"
    >
    </ChangeManualQCStatusModal>
    <RecentChangesModal
      v-if="showRecentChangesModal"
      :show="showRecentChangesModal"
      table-name="media"
      @close-modal="closeRecentChangesModal"
      @undo-change="handleUndoChange"
    />
    <MissingAssetsReportModal
      v-if="showMissingAssetsReport"
      :show="showMissingAssetsReport"
      @close-modal="showMissingAssetsReport = false"
    />
    <!-- Shortform preview modal — opens with the asset's playout file via a
         presigned URL so the operator can eyeball before approving without
         leaving the table. -->
    <b-modal
      :active.sync="showPreviewModal"
      has-modal-card
      trap-focus
      :can-cancel="['escape', 'outside']"
      @close="closePreview"
    >
      <div class="modal-card preview-modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ previewItem ? previewItem.title : 'Preview' }}
          </p>
          <button type="button" class="delete" @click="closePreview"></button>
        </header>
        <section class="modal-card-body">
          <div v-if="previewLoading" class="has-text-centered" style="padding: 60px 0;">
            <b-loading :is-full-page="false" :active="true" />
          </div>
          <div v-else-if="previewError" class="has-text-centered has-text-danger" style="padding: 40px 0;">
            {{ previewError }}
          </div>
          <video
            v-else-if="previewUrl"
            :src="previewUrl"
            controls
            autoplay
            class="preview-video"
          />
        </section>
        <footer class="modal-card-foot" style="justify-content: space-between;">
          <span class="has-text-grey is-size-7" v-if="previewItem">
            {{ previewItem.storage_path || '' }}
          </span>
          <button class="button" @click="closePreview">Close</button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CardComponent from '@/components/CardComponent'
import BaseSelect from '@/components/BaseSelect'
import ChangeManualQCStatusModal from '@/components/ChangeManualQCStatusModal'
import RecentChangesModal from '@/components/RecentChangesModal'
import MissingAssetsReportModal from '@/components/MissingAssetsReportModal'
import MediaApi from '@/api/media'
import ChannelApi from '@/api/channels'
import storageBrowseApi from '@/api/storageBrowse'
import transcodeJobsApi from '@/api/transcodeJobs'
import UnmatchedShortformUploadsPanel from '@/components/UnmatchedShortformUploadsPanel'
import Filters from '@/mixins/filters'
import { isMediaTxReady } from '@/utils/mediaStatus'

// Module-level constant — Vue's `methods` block must contain functions
// only (each entry gets .bind(this) applied at init), so this lives
// outside the component. Used by isBrowserPlayable() below.
const BROWSER_PLAYABLE_EXTS = ['mp4', 'm4v', 'webm', 'ogg', 'ts']
export default {
  name: 'ManageMedia',
  components: {
    CardComponent,
    BaseSelect,
    ChangeManualQCStatusModal,
    RecentChangesModal,
    MissingAssetsReportModal,
    UnmatchedShortformUploadsPanel
  },
  mixins: [Filters],
  metaInfo: {
    title: 'Manage Titles'
  },
  computed: {
    ...mapGetters(['user']),
    anySelected() {
      if (this.filteredMedia && this.filteredMedia.length > 0) {
        return this.filteredMedia.find(item => item.selected)
      }
      return false
    },
    allSelected() {
      if (this.filteredMedia && this.filteredMedia.length > 0) {
        return this.filteredMedia.every(item => item.selected)
      }
      return false
    },
    activeMediaType() {
      return this.mediaTypes[this.activeTabIndex] || 'series'
    },
    // Shortform = non-episodic single-asset media types. Drives the column
    // swap in the table so promos/fillers/TVCs/GFX get the lifecycle-focused
    // columns and series/movies keep the legacy metadata-focused layout.
    isShortform() {
      return ['promo', 'filler', 'tvc', 'gfx', 'bumper'].includes(this.activeMediaType)
    },
    filteredMedia() {
      let media = this.media

      // Filter by media type (tab)
      media = media.filter(item => item.media_type === this.activeMediaType)

      // Filter by channel - check both metadata.channels and metadata.suppliers
      // (supplier = channel name in BMS architecture)
      if (this.channelFilter != '') {
        media = media.filter(item => {
          if (!item || !item.metadata) return false
          const channels = item.metadata.channels || []
          const suppliers = item.metadata.suppliers || []
          if (channels.includes(this.channelFilter)) return true
          if (channels[0] && channels[0].name === this.channelFilter) return true
          if (suppliers.includes(this.channelFilter)) return true
          return false
        })
      }

      // Filter by search term
      if (this.search !== '' && this.media.length > 0) {
        let query = this.search.toLowerCase()
        media = media.filter(item => item.title.toLowerCase().includes(query))
      }

      // Sort alphabetically by title
      const sorted = [...media].sort((a, b) => {
        const titleA = (a.title || '').toLowerCase()
        const titleB = (b.title || '').toLowerCase()
        return titleA.localeCompare(titleB)
      })
      return sorted
    },
    // Get count of media items per type (for tab badges)
    mediaTypeCounts() {
      const counts = {
        series: 0,
        movie: 0,
        tvc: 0,
        promo: 0,
        filler: 0,
        gfx: 0
      }
      this.media.forEach(item => {
        if (counts.hasOwnProperty(item.media_type)) {
          counts[item.media_type]++
        }
      })
      return counts
    }
  },
  async created() {
    this.fetchMedia()
    const channels = await ChannelApi.getAll()
    this.channels = channels.map(item => item.name)
    this.channelObjects = channels
    // Load the storage configs the operator has access to (via Manage Storage).
    // Used to resolve bucket_name → storage_config_id when previewing assets,
    // so the preview button only works on buckets the operator is allowed to
    // see. Non-fatal if unavailable — preview button just stays disabled.
    try {
      this.storageConfigs = await storageBrowseApi.getConfigs()
    } catch (e) {
      console.warn('Could not load storage configs:', e)
      this.storageConfigs = []
    }
  },
  watch: {
    // When search changes, check if results exist in a different tab and switch to it
    search(newSearch) {
      if (newSearch && newSearch.length >= 2) {
        const query = newSearch.toLowerCase()
        // Check if any results in current tab
        const currentTabResults = this.media.filter(
          item => item.media_type === this.activeMediaType && item.title.toLowerCase().includes(query)
        )
        // If no results in current tab, find which tab has results
        if (currentTabResults.length === 0) {
          for (let i = 0; i < this.mediaTypes.length; i++) {
            const type = this.mediaTypes[i]
            const resultsInType = this.media.filter(
              item => item.media_type === type && item.title.toLowerCase().includes(query)
            )
            if (resultsInType.length > 0) {
              this.activeTabIndex = i
              break
            }
          }
        }
      }
    },
    // Lazy-load workflow templates the first time the operator activates
    // a shortform tab — kept off the Series-tab initial render so the page
    // load stays as fast as before.
    isShortform: {
      handler(active) {
        if (active && !this.workflowTemplatesLoaded) {
          this.loadWorkflowTemplates()
        }
      },
      immediate: true
    }
  },
  data() {
    return {
      loading: false,
      media: [],
      channels: [],
      search: '',
      showConfirmModal: false,
      item: {},
      channelFilter: '',
      activeTabIndex: 0, // Default to series tab (index 0)
      mediaTypes: ['series', 'movies', 'tvc', 'promo', 'filler', 'gfx'],
      // Full channel objects {id, name, ...} kept separately from the names-
      // only `channels` array used by the legacy Filter dropdown — the
      // Create-from-file modal needs ids to send `channels: [{id, name}]`
      // to the backend's addChannels handler.
      channelObjects: [],
      showChangeManualQCStatusModal: false,
      showRecentChangesModal: false,
      showMissingAssetsReport: false,
      // Shortform inline UX state
      showPreviewModal: false,
      previewItem: null,
      previewUrl: null,
      previewLoading: false,
      previewError: null,
      approveBusy: {}, // mediaId → bool while PUT in flight, blocks double-tap
      storageConfigs: [], // loaded from /api/storage-browse/configs on mount; bucket_name → id lookup
      // Transcode submission state (Commit 4c).
      // workflowTemplates loaded lazily on first shortform tab activation —
      // most operators stay on Series so we don't fetch unless needed.
      workflowTemplates: [],
      workflowTemplatesLoaded: false,
      transcodeSubmitting: {} // mediaId → bool while submit in flight
    }
  },
  methods: {
    ...mapActions(['getUser']),
    isMediaTxReady,
    toggleSelectAll() {
      const newValue = !this.allSelected
      // Use Vue.set for proper reactivity on filtered items
      this.filteredMedia.forEach(item => {
        this.$set(item, 'selected', newValue)
      })
      // Force reactivity update
      this.media = [...this.media]
    },
    changeManualStatus(item) {
      this.item = item
      this.showChangeManualQCStatusModal = true
    },
    deleteTitles() {
      let items = this.filteredMedia.filter(item => item.selected)
      this.$buefy.dialog.confirm({
        message: `Are you sure you want to delete the ${items.length} titles?`,
        onConfirm: async () => {
          this.loading = true
          await MediaApi.removeMultiple(items.map(item => item.id))
          this.$buefy.toast.open({
            message: 'Titles removed successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          this.loading = false
          this.media = this.media.filter(item => !item.selected)
        }
      })
    },
    publish(item) {
      MediaApi.publish({
        mediaId: item.id,
        published: item.published === 0 ? 1 : 0
      })
        .then(() => {
          let found = this.media.find(mediaItem => mediaItem.id === item.id)
          found.published = item.published === 0 ? 1 : 0
        })
        .catch(err => {
          console.log(err)
          this.$buefy.toast.open({
            message: 'Error changing media publish status',
            position: 'is-top',
            type: 'is-danger'
          })
        })
    },
    markAsFeatured(item) {
      MediaApi.markAsFeatured({
        mediaId: item.id,
        featured: item.featured === 0 ? 1 : 0
      })
        .then(() => {
          let found = this.media.find(mediaItem => mediaItem.id === item.id)
          found.featured = item.featured === 0 ? 1 : 0
        })
        .catch(err => {
          console.log(err)
          this.$buefy.toast.open({
            message: 'Error changing media featured status',
            position: 'is-top',
            type: 'is-danger'
          })
        })
    },
    isPublished(item) {
      return item && item.published === 1
    },
    isFeatured(item) {
      return item && item.featured === 1
    },
    confirmDelete(item) {
      this.showConfirmModal = true
      this.item = item
    },
    deleteTitle() {
      MediaApi.remove({
        mediaId: this.item.id
      })
        .then(() => {
          this.$buefy.toast.open({
            message: 'Media deleted successfully!',
            position: 'is-top',
            type: 'is-success'
          })
          this.fetchMedia()
          this.showConfirmModal = false
          this.loading = false
        })
        .catch(err => {
          console.log(err)
          this.$buefy.toast.open({
            message: 'Error deleting media',
            position: 'is-top',
            type: 'is-danger'
          })
          this.loading = false
        })
    },
    fetchMedia() {
      this.loading = true
      MediaApi.getUserMedia()
        .then(response => {
          // Handle both paginated response { data: [...] } and legacy array response
          const media = Array.isArray(response) ? response : (response.data || [])
          media.forEach(item => {
            if (item.mediadata && item.mediadata !== '') {
              item.mediadata = JSON.parse(item.mediadata)
            }
            if (item.metadata && item.metadata !== '') {
              item.metadata = JSON.parse(item.metadata)
            }
          })
          // Sort alphabetically by title
          media.sort((a, b) => {
            const titleA = (a.title || '').toLowerCase()
            const titleB = (b.title || '').toLowerCase()
            return titleA.localeCompare(titleB)
          })
          this.media = media
          this.loading = false
        })
        .catch(err => {
          console.error('Error fetching media:', err)
          this.$buefy.toast.open({
            message: 'Error loading media. Please refresh the page.',
            position: 'is-top',
            type: 'is-danger',
            duration: 5000
          })
          this.loading = false
        })
    },
    showRecentChanges() {
      this.showRecentChangesModal = true
    },
    closeRecentChangesModal() {
      this.showRecentChangesModal = false
    },
    handleUndoChange() {
      // Refresh media list after undo
      this.fetchMedia()
      this.$buefy.toast.open({
        message: 'Change undone successfully',
        type: 'is-success',
        position: 'is-top'
      })
    },
    getQcStatusType(status) {
      switch (status) {
        case 'passed': return 'is-success'
        case 'passed_with_warnings': return 'is-warning'
        case 'overridden': return 'is-info'
        case 'failed': return 'is-danger'
        case 'processing': return 'is-light'
        case 'pending': return 'is-light'
        default: return 'is-light'
      }
    },
    openQcReport(item) {
      if (item.qc_report_url) {
        window.open(item.qc_report_url, '_blank')
      }
    },
    getMediaTypeCount(mediaType) {
      return this.mediaTypeCounts[mediaType] || 0
    },

    // ====================================================================
    // SHORTFORM INLINE UX (Commit 4)
    // ====================================================================

    // Duration helpers — prefer the new probed duration_ms column, fall
    // back to mediadata.mediaLength (legacy, unit-inconsistent). When
    // showing the legacy value we keep it as the operator typed it (raw
    // string), because units differ per tenant.
    hasDuration(row) {
      if (!row) return false
      if (Number.isFinite(Number(row.duration_ms)) && Number(row.duration_ms) > 0) return true
      const legacy = row.mediadata && row.mediadata.mediaLength
      return legacy != null && String(legacy).length > 0
    },
    formatDuration(row) {
      if (!row) return ''
      if (Number.isFinite(Number(row.duration_ms)) && Number(row.duration_ms) > 0) {
        return (Number(row.duration_ms) / 1000).toFixed(2) + 's'
      }
      const legacy = row.mediadata && row.mediadata.mediaLength
      return legacy != null && String(legacy).length > 0 ? String(legacy) : ''
    },

    // Source link state — needs BOTH source_filename and storage_path set.
    // storage_path alone is preview-capable but doesn't count as a proper
    // source link (spot processing won't pick it up).
    hasSourceLinked(row) {
      return !!(row && row.source_filename && row.storage_path)
    },

    // ====================================================================
    // SOURCE vs TX-READY (Patch 1 — informational only, operator decides)
    // ====================================================================
    //
    // Two orthogonal signals on a shortform row:
    //
    //   1. PROVENANCE — has this file been through BMS transcode, or is
    //      it still the raw delivery? Tracked by media.tx_file_path
    //      (populated by transcode worker via Patch 2; until then NULL
    //      on every row).
    //
    //   2. BROWSER-PLAYABILITY — purely about the modal video player.
    //      MXF / MOV won't render in <video>; MP4/M4V/TS/WebM will.
    //      Independent of whether the file is "Cinegy playout ready".
    //
    // The OPERATOR decides whether the file is good to ship. Source
    // files are sometimes broadcast-ready as-delivered (some agencies
    // deliver interlaced MP4 to spec). Both buttons stay enabled when a
    // file is linked — operator picks:
    //
    //   "Approve as-is" → vouches the source IS TX-ready (no transcode)
    //   "Submit transcode" → BMS produces the TX file
    //   (after either, the file can be staged to Cinegy)
    //
    // The status dot reflects the LIFECYCLE (linked → approved → staged)
    // not the format. A "Source MP4" badge near the dot tells the
    // operator the file hasn't been BMS-transcoded, but doesn't block
    // any action.

    // Return the path of the file we'd play / stage / approve. Prefers
    // tx_file_path once Patch 2 wires it; storage_path is the fallback
    // (and the only thing populated today).
    getPlayablePath(row) {
      if (!row) return null
      return row.tx_file_path || row.storage_path || null
    },

    extensionOf(pathStr) {
      if (!pathStr) return ''
      const m = String(pathStr).match(/\.([^./]+)$/)
      return m ? m[1].toLowerCase() : ''
    },

    // Browser-playable formats live as the module-level
    // BROWSER_PLAYABLE_EXTS constant above the component definition.
    // Strictly about playback, NOT about TX-readiness for Cinegy.
    isBrowserPlayable(row) {
      const path = this.getPlayablePath(row)
      if (!path) return false
      return BROWSER_PLAYABLE_EXTS.includes(this.extensionOf(path))
    },

    // True ONLY when the file has been through BMS transcode (tx_file_path
    // populated, set by Patch 2's result poller) OR is a pre-existing
    // approval (legacy backward-compat — 11 rows in production today).
    // Used to surface the "Source / Transcoded" badge on the dot, NOT to
    // gate any operator action.
    hasBmsTranscodedOutput(row) {
      if (!row) return false
      if (row.tx_file_path) return true
      // Legacy fallback: pre-Patch-2 approved rows hold their TX in
      // storage_path. Their prior approval is the operator's vouch.
      if (row.approved_for_playout && row.storage_path) return true
      return false
    },

    // Four-state status dot for shortform rows. Reflects LIFECYCLE only —
    // not file format. Operator can approve a source file as-is (no
    // transcode needed) OR transcode then approve; either way the dot
    // moves through yellow → blue → green.
    //
    //   grey   nothing linked yet
    //   yellow file linked, not yet approved (operator review pending)
    //   blue   approved, awaiting SMB staging
    //   green  approved AND staged for playout
    //
    // Duration-missing is a separate concern surfaced as a ⚠ on the
    // Duration column — applies at any state but doesn't change the dot.
    shortformStatus(row) {
      if (!row) return { state: 'grey', label: 'No data' }
      const hasFile = !!this.getPlayablePath(row)
      const approved = !!row.approved_for_playout
      const staged = row.smb_staging_status === 'staged'
      const bmsTranscoded = this.hasBmsTranscodedOutput(row)
      const provenanceNote = bmsTranscoded ? '' : ' (source file — not BMS-transcoded)'
      if (!hasFile) return { state: 'grey', label: 'No file linked' }
      if (!approved) return { state: 'yellow', label: 'File linked — awaiting Approve for Playout' + provenanceNote }
      if (!staged) return { state: 'blue', label: 'Approved — awaiting playout staging' + provenanceNote }
      return { state: 'green', label: 'Approved and staged for playout' + provenanceNote }
    },

    // Inline approve toggle. Available once the row has ANY file linked
    // (source or transcoded) AND duration is set. Operator vouches that
    // the file is playout-ready — sometimes the source is good as-is.
    canToggleApprove(row) {
      return !!this.getPlayablePath(row) && this.hasDuration(row)
    },
    approveTooltip(row) {
      if (!this.getPlayablePath(row)) return 'Link a source file first'
      if (!this.hasDuration(row)) return 'Duration missing — set before approving'
      if (row.approved_for_playout) return 'Approved for Playout'
      if (!this.hasBmsTranscodedOutput(row)) {
        return 'Approve for Playout (source file — vouches it\'s broadcast-ready as-delivered)'
      }
      return 'Approve for Playout'
    },
    async onApproveToggle(row, newValue) {
      // Optimistic flip — write to row first so the UI updates immediately,
      // PUT in the background, revert on failure. Vue 2 reactivity needs
      // $set for the busy map; the row field is already reactive from
      // fetchMedia.
      const previousValue = !!row.approved_for_playout
      // MySQL returns 0/1 for the boolean — normalise to true/false when
      // we write back so the b-switch :value coerces consistently.
      row.approved_for_playout = !!newValue
      this.$set(this.approveBusy, row.id, true)
      try {
        // Use the dedicated single-field endpoint — PUT /api/media with a
        // partial payload would write storage_path=null and V2-reset the
        // operator's source link. setApproved only touches the approval
        // columns, never storage / QC / staging.
        await MediaApi.setApproved(row.id, !!newValue)
        this.$buefy.toast.open({
          message: newValue ? 'Approved for Playout' : 'Approval removed',
          type: newValue ? 'is-success' : 'is-warning',
          duration: 3000
        })
      } catch (err) {
        // Revert on failure — back to previous state
        row.approved_for_playout = previousValue
        this.$buefy.toast.open({
          message: 'Could not save approval — ' + (err && err.data && err.data.err ? err.data.err : 'network error'),
          type: 'is-danger',
          duration: 4000
        })
      } finally {
        this.$set(this.approveBusy, row.id, false)
      }
    },

    // Parse the s3://{bucket}/{key} storage_path layout that addMedia and the
    // auto-link path both write. Returns null on a non-s3 path (e.g. a raw
    // file_key without protocol — shouldn't happen for shortform but we
    // surface it as an error rather than crash.)
    parseStoragePath(storagePath) {
      if (!storagePath) return null
      const m = String(storagePath).match(/^s3:\/\/([^/]+)\/(.+)$/)
      if (!m) return null
      return { bucket: m[1], key: m[2] }
    },

    // Map bucket_name → storage_config_id using the configs the operator was
    // granted access to via Manage Storage. If the bucket isn't in their list,
    // the preview must refuse — same access boundary used by storage-browse.
    findStorageConfigIdForBucket(bucketName) {
      if (!bucketName || !this.storageConfigs) return null
      const cfg = this.storageConfigs.find(c => c.bucket_name === bucketName)
      return cfg ? cfg.id : null
    },

    // Tooltip for the ▶ preview button. Only disabled when the browser
    // genuinely can't render the file (MXF / MOV / etc.). Source MP4
    // plays fine — operator wants to eyeball it before approving or
    // before deciding to transcode.
    previewTooltip(row) {
      if (!row || !this.getPlayablePath(row)) return 'No file to preview yet'
      if (!this.isBrowserPlayable(row)) {
        const ext = this.extensionOf(this.getPlayablePath(row)) || 'unknown'
        return `${ext.toUpperCase()} not browser-playable — open EditMedia or download to inspect`
      }
      if (!this.hasBmsTranscodedOutput(row)) {
        return 'Preview source file (not BMS-transcoded — what plays after transcode may differ)'
      }
      return 'Preview TX file'
    },

    // Preview modal — plays the row's TX-ready file. Prefers tx_file_path
    // (future-state, once Patch 2 wires the transcode poller) and falls
    // back to storage_path (legacy / direct-TX-delivery rows). Goes
    // through the EXISTING storageBrowseApi.getPresignedUrl path so the
    // same access gating as ManageStorage applies.
    async openPreview(row) {
      this.previewItem = row
      this.previewUrl = null
      this.previewError = null
      this.previewLoading = true
      this.showPreviewModal = true
      try {
        const playablePath = this.getPlayablePath(row)
        const parsed = this.parseStoragePath(playablePath)
        if (!parsed) {
          // ~13% of shortform rows in production use a raw path (no s3://
          // prefix — historical / batch-import shape). They need bucket
          // disambiguation we can't do from the row alone. Point operator
          // at EditMedia where MediaSourceLinker can resolve via the
          // operator-selected storage config.
          throw new Error('Legacy path format — open EditMedia for preview')
        }
        const configId = this.findStorageConfigIdForBucket(parsed.bucket)
        if (!configId) {
          throw new Error(`No storage config granted for bucket "${parsed.bucket}" — ask the tenant admin to grant access in Manage Storage`)
        }
        const result = await storageBrowseApi.getPresignedUrl(configId, parsed.key)
        this.previewUrl = result && (result.url || result.presignedUrl) || null
        if (!this.previewUrl) {
          throw new Error('Backend did not return a presigned URL')
        }
      } catch (err) {
        this.previewError = err && err.message ? err.message : 'Could not load preview'
      } finally {
        this.previewLoading = false
      }
    },
    closePreview() {
      this.showPreviewModal = false
      this.previewUrl = null
      this.previewItem = null
      this.previewError = null
    },

    // UnmatchedShortformUploadsPanel emits this after creating a media
    // row from a detection. Refresh the main table so the new row shows
    // up under the active tab. fetchMedia already filters by org so we
    // don't have to do anything tab-specific.
    onMediaCreatedFromUpload(event) {
      this.fetchMedia()
      // Optional nice-to-have: toast confirmation handled inside the
      // child component already, so nothing else to do here.
    },

    // ====================================================================
    // INLINE TRANSCODE (Commit 4c)
    // ====================================================================

    // Mirrors MediaSourceLinker.loadWorkflowTemplates — single-file
    // workflows only (shortform never goes through the stitch pipeline).
    // Auto-selects format_convert as default; if absent, first available.
    async loadWorkflowTemplates() {
      if (this.workflowTemplatesLoaded) return
      try {
        const templates = await transcodeJobsApi.getAvailableWorkflows()
        this.workflowTemplates = (templates || []).filter(
          t => t.supports_single_file_input
        )
        this.workflowTemplatesLoaded = true
      } catch (e) {
        console.warn('Could not load workflow templates:', e)
        this.workflowTemplates = []
        this.workflowTemplatesLoaded = true // Don't retry on every tab switch
      }
    },

    // Pick the workflow template to use for a transcode submission. Same
    // priority as MediaSourceLinker: format_convert first, else the first
    // single-file workflow the org has access to.
    pickWorkflowTemplate() {
      if (!this.workflowTemplates || !this.workflowTemplates.length) return null
      const formatConvert = this.workflowTemplates.find(
        t => t.template_code === 'format_convert'
      )
      return formatConvert || this.workflowTemplates[0]
    },

    // Transcode submit. Available whenever a source file is linked and
    // the org has a workflow + bucket access. Even an already-BMS-
    // transcoded row stays submittable — operator might need to re-run
    // with a different preset, recover from a previously-bad output,
    // etc. A confirm dialog surfaces the "you've already transcoded
    // this" warning before firing the job.
    canSubmitTranscode(row) {
      if (!row || !row.storage_path) return false
      if (!this.hasSourceLinked(row)) return false
      const parsed = this.parseStoragePath(row.storage_path)
      if (!parsed) return false
      const configId = this.findStorageConfigIdForBucket(parsed.bucket)
      if (!configId) return false
      const workflow = this.pickWorkflowTemplate()
      return !!workflow
    },
    transcodeTooltip(row) {
      if (!this.hasSourceLinked(row)) return 'Link a source file first'
      const parsed = this.parseStoragePath(row.storage_path)
      if (!parsed) return 'Legacy storage path format — open EditMedia to transcode'
      const configId = this.findStorageConfigIdForBucket(parsed.bucket)
      if (!configId) return `No storage config granted for bucket "${parsed.bucket}"`
      const workflow = this.pickWorkflowTemplate()
      if (!workflow) return 'No transcode workflow available — ask Super Admin to grant access'
      if (this.hasBmsTranscodedOutput(row)) {
        return 'Re-submit transcode (existing TX file will be overwritten)'
      }
      return 'Submit transcode — produces TX file (interlaced, EBU R128, container normalized)'
    },

    // Submit a single-file transcode job for the row. Uses the same
    // endpoint and payload shape as MediaSourceLinker.submitForTranscode
    // so the worker pipeline behaviour is byte-identical (QC + EBU R128
    // loudness normalization + encode all built into the worker).
    submitTranscode(row) {
      const parsed = this.parseStoragePath(row.storage_path)
      if (!parsed) {
        this.$buefy.toast.open({
          message: 'Storage path is not s3:// — open EditMedia to transcode',
          type: 'is-warning',
          duration: 4000
        })
        return
      }
      const configId = this.findStorageConfigIdForBucket(parsed.bucket)
      if (!configId) {
        this.$buefy.toast.open({
          message: `No storage config for bucket ${parsed.bucket}`,
          type: 'is-danger',
          duration: 4000
        })
        return
      }
      const workflow = this.pickWorkflowTemplate()
      if (!workflow) {
        this.$buefy.toast.open({
          message: 'No transcode workflow available — ask Super Admin to grant access',
          type: 'is-danger',
          duration: 5000
        })
        return
      }

      // Warn before re-transcoding an already-transcoded row — easy to
      // misclick on a 100+ row table. Approve toggle stays available
      // regardless; this is purely about wasting worker time/$.
      const isRetranscode = this.hasBmsTranscodedOutput(row)
      const retranscodeWarning = isRetranscode
        ? `<div style="background:#fef3c7; border:1px solid #d97706; padding:8px 10px; border-radius:4px; margin-bottom:10px;">
             <strong>⚠ Re-transcode:</strong> this row already has a TX
             file. Submitting will produce a new output that will replace
             the existing one. Operator must re-approve after the new
             transcode completes.
           </div>`
        : ''
      this.$buefy.dialog.confirm({
        title: isRetranscode ? 'Re-submit transcode' : 'Submit for transcode',
        message: `${retranscodeWarning}
          Submit <strong>${row.title}</strong> for transcode via
          <strong>${workflow.template_name || workflow.template_code}</strong>?
          <br><br>
          <small style="color:#666;">
            QC + EBU R128 loudness normalization + encode all run in the
            worker. Status updates live in EditMedia; the row here will
            show the new playout file once the job completes.
          </small>`,
        confirmText: isRetranscode ? 'Re-transcode' : 'Submit',
        type: isRetranscode ? 'is-warning' : 'is-info',
        onConfirm: async () => {
          this.$set(this.transcodeSubmitting, row.id, true)
          try {
            const result = await transcodeJobsApi.submitJob({
              workflowTemplateId: workflow.id,
              inputStorageConfigId: configId,
              inputPath: parsed.key,
              mediaId: row.id
            })
            this.$buefy.toast.open({
              message: `Transcode job submitted (${result.jobUuid ? result.jobUuid.substring(0, 8) : 'queued'})`,
              type: 'is-success',
              duration: 4000
            })
          } catch (err) {
            const msg =
              (err && err.response && err.response.data && err.response.data.error) ||
              (err && err.message) ||
              'Failed to submit transcode'
            this.$buefy.toast.open({
              message: 'Submit failed — ' + msg,
              type: 'is-danger',
              duration: 5000
            })
          } finally {
            this.$set(this.transcodeSubmitting, row.id, false)
          }
        }
      })
    }
  }
}
</script>

<style>
.table th,
.table td {
  font-size: 12px;
}
.ml-2 {
  margin-left: 0.5rem;
}

/* Media type tabs styling */
.b-tabs .tab-content {
  padding: 0;
}

.b-tabs .tabs {
  margin-bottom: 1rem;
}

.b-tabs .tabs li a .tag {
  margin-left: 0.5rem;
  font-size: 0.7rem;
}

.media-title-link {
  font-size: 14px !important;
  font-weight: 600 !important;
}

.synopsis-cell {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 300px;
}

/* TX-readiness dot for non-series rows. Matches EditSchedule week-view dot
   and the day-view inline dot in BroadcastTimeline. */
.tx-status-dot-inline {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.3);
  vertical-align: middle;
}
.tx-status-dot-inline.tx-ready {
  background-color: #22c55e;
}
.tx-status-dot-inline.tx-missing {
  background-color: #ef4444;
}
/* Shortform 5-state roll-up. Title tooltip carries the human-readable
   state; the dot is the at-a-glance signal across the new operational
   columns. Same dimensions as .tx-status-dot-inline above so the column
   alignment is identical between series and shortform tabs. */
.tx-status-dot-inline.shortform-state-grey {
  background-color: #9ca3af;
}
.tx-status-dot-inline.shortform-state-red {
  background-color: #ef4444;
}
.tx-status-dot-inline.shortform-state-yellow {
  background-color: #eab308;
}
.tx-status-dot-inline.shortform-state-blue {
  background-color: #3b82f6;
}
.tx-status-dot-inline.shortform-state-green {
  background-color: #22c55e;
}
/* Shortform column cell treatments. */
.tx-filename-cell {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 11px;
  color: #4b5563;
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.source-linked {
  color: #22c55e;
  font-weight: bold;
}
.source-link-action {
  color: #3b82f6;
  font-weight: 500;
  text-decoration: underline;
}
.duration-missing {
  color: #eab308;
  margin-left: 4px;
  cursor: help;
}
.preview-modal-card {
  width: 80vw;
  max-width: 960px;
}
.preview-modal-card .preview-video {
  width: 100%;
  max-height: 70vh;
  background: #000;
}
</style>
